import EventEmitter from 'eventemitter3';
import { ITrimEditorManagerBase } from '../trim-editor.manager';
import { TrimEditableImage } from '../image-edit/trim.editable-image';
import { TrimPrint } from '../../print/trim.print';
import { TextFont, TextInfo, TextOption } from '../../../components/pages/layout/toolbar/layout.free-text';
import { DisplayObject } from 'createjs-module';
import { XmlStructureModel } from '../../../xml/model/xml-structure-model';
import { AlbumSettingData } from '../../../slices/layout-slice';
import { CheckFinishType } from '../validation/trim.validation.manager';
import { PreviewListType } from '../../../components/pages/layout/image-preview/layout.image-preview';
import { Pos } from '../../../models/pos';
import { AdditionalShowType } from '../../../components/pages/layout/toolbar/layout.toolbar';
import { PostCardType } from '../../../components/pages/layout/select-template/layout.select-template';
import { Orientation } from 'get-orientation/browser';
import { TrimCjContainer } from '../../model/cj-factory';
import { FileDetail } from '../../../api/front/cloud-folder/api-get-cloud-folder-file-detail';
import { OrderParam } from '../../../layout-editor/albam/albam';
import { PrintProcessList, PrintUploadProgressList } from '../image-processor/trim.image-processor.manager';
import { PrintImagesDataEntity } from '../../../api/sift/front/api-print-image';
import { ScreenColorType } from '../../../components/pages/layout/menu/layout.menu';
import { TextEditType } from '../image-edit/text/trim.text-controller';
import { PrintTrimMenuType } from '../../../components/pages/sift/print-trim/hooks/use-print-trim';

/**
 * シーケンスの方向
 *  t : PrintTrimEditor
 *  r : React
 */
type EventType = {
  // 選択
  't->r:select': (param: {
    image?: DisplayObject,
    pos?: { x: number, y: number, scale?: number, },
    isImgDisabled?: boolean
  }) => void,
  // 選択解除
  't->r:dis-select': () => void,
  // 選択解除
  'r->t:dis-select': () => void,
  // 移動モードの切り替え
  // 'r->t:move-mode': (param: { moveMode: boolean }) => void,
  // 移動
  't->r:move': (param: { x: number, y: number }) => void,
  // Shift移動
  'r->t:shift-move': (param: { x: number, y: number, type?: 'image' }) => void,
  't->r:shift-move:push': (param: { shiftNum: number }) => void,
  't->r:shift-move:change': (param: { shiftNum: number }) => void,
  't->r:shift-move:get': (param: { callback: (p: { shiftNum: number }) => void }) => void,
  // 写真枠フィット
  't->r:frame-fit:push': (param: { fitNum: number }) => void,
  't->r:frame-fit:change': (param: { fitNum: number }) => void,
  't->r:frame-fit:get': (param: { callback: (p: { fitNum: number }) => void }) => void,
  // 移動モード
  'r->t:move-mode': () => void,
  // 回転
  'r->t:rotate': (param: { angle: number, type?: 'image' }) => void,
  // 画像挿入
  'r->t:insert-image': (param: { files: File[] }) => void,
  // テキスト挿入開始
  'r->t:insert-text:start': (param: { callback: (isOpen: boolean, info: TextInfo) => void }) => void,
  // テキスト挿入終了
  'r->t:insert-text:end': (param: {}) => void,
  // 中心線の表示切り替え
  'r->t:center-line': (param: { display: boolean }) => void,
  // ルーラーの表示切り替え
  'r->t:ruler': (param: { display: boolean }) => void,
  // 拡大/縮小（図形）
  'r->t:resize': (param: { scale: number }) => void,
  // 拡大/縮小（viewer）
  'r->t:zoom': (param: { lv: number }) => void,
  'r->t:zoom:direct': (param: { scale: number }) => void,
  't->r:zoom:change': (param: { scale: number, x: number, y: number }) => void,
  // 拡大 1 段階アップ
  'r->t:zoom:lv:up': (param: {}) => void,
  // 拡大 1 段階ダウン
  'r->t:zoom:lv:down': (param: {}) => void,
  // 拡大/縮小 フィット
  'r->t:zoom:fit': (param: {}) => void,
  // 拡大/縮小 lv 検知
  't->r:zoom:lv:change': (param: { lv: number }) => void,
  't->r:zoom:scale:change': (param: { scale: number }) => void,
  // 拡大/縮小 lv 取得
  't->r:zoom:lv:get': (param: { callback: (p: { lv: number }) => void }) => void,
  // 削除
  'r->t:delete': () => void,
  // リセット
  'r->t:reset': () => void,
  // 複製
  'r->t:clone': () => void,
  // undo
  'r->t:undo': () => void,
  // redo
  'r->t:redo': () => void,
  // ダイアログの表示
  't->r:push-dialog': (param: {
    title: string,
    message: string[],
    buttons: {
      label: string,
      callback: () => void,
      options?: {
        waitActive?: number,
      },
    }[],
    remind?: {
      callback: (v: boolean) => void;
      label?: string;
      initCheck?: boolean;
    },
  }) => void,
  // 画像の追加
  'r->t:add-image': (param: {
    files: File[],
    kijshopCd: string,
    shopOrderId: string,
    orderId: string | null,
    restore?: boolean,
    path?: string,
    selectId?: string,
    onUploaded?: (p: { editableImage: TrimEditableImage, index: number, sameImage?: boolean }) => void,
    onCreatedEditableImage?: (p: { editableImage: TrimEditableImage, index: number, sameImage?: boolean }) => void,
    isUseCheck?: boolean,
    selectIdList?: { path: string, selectID: string }[],
    samePushIgnore?: boolean,
    orientation?: Orientation,
    cfImageId?: string | null
  }) => void,
  'r->t:trim-add-image': (param: {
    files: File[],
    kijshopCd: string,
    shopOrderId: string,
    restore?: boolean,
    path?: string,
    selectId?: string,
    onUploaded?: (p: { editableImage: TrimEditableImage, index: number, sameImage?: boolean }) => void,
    onCreatedEditableImage?: (p: { editableImage: TrimEditableImage, index: number, sameImage?: boolean }) => void,
  }) => void,
  'r->t:add-image-png': (param: {
    files: File[],
    kijshopCd: string,
    shopOrderId: string,
    orderId: string | null,
    restore?: boolean,
    path?: string,
    selectId?: string,
    onUploaded?: (p: { editableImage: TrimEditableImage, index: number }) => void,
    onCreatedEditableImage?: (p: { editableImage: TrimEditableImage, index: number }) => void,
    isUseCheck?: boolean
  }) => void,
  'r->t:shortcut:add-image-png': () => void,
  'r->t:add-image-comp': (param: {
    files: File[],
    kijshopCd: string,
    shopOrderId: string,
    orderId: string | null,
    restore?: boolean,
    path?: string,
    callback?: (p: { editableImage: TrimEditableImage }) => void
  }) => void,
  // 画像の復元
  'r->t:add-image:restore': (param: {
    kijshopCd: string,
    shopOrderId: string,
    orderId: string | null,
    list: any[],
    onRestoreOne: () => void,
    isDelete: boolean,
    cfImageId?: string | null
  }) => void,
  'r->t:trim-add-image:restore': (param: {
    kijshopCd: string,
    shopOrderId: string,
    data: PrintImagesDataEntity,
    onSuccess: (editableImage: TrimEditableImage) => void,
    onError?: () => void,
  }) => void,
  // レイアウトサムネイル画像の追加
  'r->t:add-layout-comp': (param: {
    file: File,
    kijshopCd: string,
    shopOrderId: string,
    orderId: string | null,
    restore?: boolean,
    path?: string,
    callback?: (p: { editableImage: TrimEditableImage }) => void,
    onError: () => void
  }) => void,
  // ロゴ画像の追加
  'r->t:add-image-logo': (param: {
    file: File,
    kijshopCd: string,
    shopOrderId: string,
    orderId: string | null,
    restore?: boolean,
    path?: string,
    callback: (p: { editableImage: TrimEditableImage }) => void,
    onCreatedEditableImage?: (p: { editableImage: TrimEditableImage }) => void,
    hashData?: {
      create?: boolean,
      hash?: string,
      checkList?: { path: string, hash: string }[],
      selectIdList?: { path: string, selectID: string }[]
    },
    samePushIgnore?: boolean
  }) => void,
  // 画像のダイレクト追加
  'r->t:add-image:direct': (param: {
    file: File,
    kijshopCd: string,
    shopOrderId: string,
    orderId: string | null,
    restore?: boolean,
    path?: string,
    callback: (p: { editableImage: TrimEditableImage }) => void,
    onCreatedEditableImage?: (p: { editableImage: TrimEditableImage }) => void
  }) => void,
  // PPM連携画像追加
  'r->t:add-image:cloud-folder': (param: {
    files: (FileDetail & { file: File | null, orderId: string })[],
    kijshopCd: string,
    shopOrderId: string,
    orderId: string | null,
    onUploaded?: (p: { editableImage: TrimEditableImage, index: number }) => void,
    onCreatedEditableImage?: (p: { editableImage: TrimEditableImage }) => void,
    isLayout?: boolean
  }) => void,
  // TrimEditableImage の list 更新
  't->r:change-editable-image-list': (param: { list: TrimEditableImage[] }) => void,
  't->r:pushed-editable-image-list': (param: { list: TrimEditableImage[] }) => void,
  // TrimEditableImage の list 取得 (Reactへの受け渡し)
  't->r:post-editable-image-list': (param: { callback: (p: { list: TrimEditableImage[] }) => void }) => void,
  // TrimEditableImage の追加 (レイアウト編集画面へ)
  'r->t:add-editable-image': (param: { editableImage: TrimEditableImage }) => void,
  // TrimEditableImage のドラッグスタート
  'r->t:editable-image:drag:start': (param: { editableImage: TrimEditableImage }) => void,
  // TrimEditableImage のドラッグエンド
  'r->t:editable-image:drag:end': (param: { editableImage: TrimEditableImage }) => void,
  // TrimEditableImage のドラッグの現在地チェック
  'r->t:editable-image:drag:check': (param: { x: number, y: number }) => void,
  // TrimEditableImage のドロップ
  'r->t:editable-image:drop': () => void,
  // ローカルからcanvasへのドロップ
  'r->t:editable-image:drop:direct': (param: {
    editableImage: TrimEditableImage,
    index: number,
    callback?: () => void
  }) => void,
  // ローカルからtemplateへのドロップ
  'r->t:editable-image:drop:temp': (param: {
    editableImage: TrimEditableImage,
    index: number,
    postCardType: PostCardType,
    callback: () => void
  }) => void,
  // 画像のごみフラグ更新
  'r->t:change-editable-image-ccd-flags': (param: { editableImageId: string, flag: boolean, isPack: boolean }) => void,
  // アルバム選択
  'r->t:add-album': (param: { album: TrimPrint, callback?: () => void, restoreData?: OrderParam }) => void,
  // アルバム編集開始
  'r->t:select-album': (param: { album: TrimPrint, callback?: () => void }) => void,
  // 画像処理の状態取得
  't->r:get-image-processor-status': (param: { status: 'busy' | 'standby', total: number, finish: number }) => void,
  // 操作モード変更
  'r->t:change-operation-mode': (param: { mode: 'pan-zoom' | 'select' }) => void,
  'r->t:shortcut:change-operation-mode': (param: { mode: 'pan-zoom' | 'select' }) => void,
  't->r:shortcut:change-operation-mode-reset': (param: { isSelect: boolean }) => void,
  // 操作カーソル変更
  'r->t:change-operation-cursor': (param: { mode: 'pan-zoom' | 'select' | 'text' }) => void,
  // 写真/写真枠切り替え
  'r->t:change-image/frame': (param: { mode: 'image' | 'image-frame' }) => void,
  't->r:change-image/frame': (param: { mode: 'image' | 'image-frame', isAdditional?: boolean }) => void,
  // フォント一覧取得の対応状況チェック
  't->r:font:check-support': (param: { callback: (p: { supported: boolean }) => void }) => void,
  // フォント一覧の取得
  't->r:font:get-list': (param: { callback: (p: { fonts: string[] }) => void }) => void,
  // ウェブフォント一覧の取得
  't->r:web-font:get-list': (param: { callback: (p: { fonts: string[] }) => void }) => void,
  // ウェブフォントのダウンロード
  't->r:web-font:download': (param: {
    fontFamilyName: string,
    callback: () => void,
    error: (msg: string) => void
  }) => void,
  // テキストの更新
  'r->t:reload-text': (param: {
    textParam: TextEditType,
    callback?: () => void
  }) => void,
  // テキストツール表示
  't->r:show-text-tool': (param: { isOpen: boolean, textInfo: TextInfo }) => void,
  // スポイト開始
  'r->t:color-dropper:start': (param: {
    container: HTMLDivElement | string,
    onChange: (p: { color: string }) => void
  }) => void,
  // スポイト終了
  'r->t:color-dropper:stop': (param: {}) => void,
  // テキスト削除
  'r->t:delete-text': (param: { id: string }) => void,
  // テキスト編集完了
  'r->t:finish-text-edit': (param: { id: string }) => void,
  // 内接・外接・ピクセル等倍切り替え
  'r->t:image-resize-in/out/eq': (param: { mode: 'in' | 'out' | 'eq' }) => void,
  't->r:image-resize-in/out/eq': (param: { mode: 'in' | 'out' | 'eq' }) => void,
  // 有効保証エリア切り替え
  'r->t:image-guide-on/off': (param: { mode: 'on' | 'off' }) => void,
  // 背景色切り替え
  'r->t:change-screen-color': (param: PrintTrimMenuType) => void,
  // 余白の許可
  'r->t:change-allow-margin': (param: { isAllowMargin: boolean }) => void,
  't->r:change-allow-margin': (param: { isAllowMargin: boolean }) => void,
  // 一時保存
  'r->t:valid:save-trim': (param: {
    kijshopCd: string,
    shopOrderId: string,
    orderId: string,
    startDate: string
  }) => void,
  'r->t:save-trim': (param: {
    kijshopCd: string,
    shopOrderId: string,
    orderId: string,
    startDate: string,
    callback: () => void
  }) => void,
  'r->t:shortcut:save-trim': () => void,
  'r->t:shortcut:save-trim:focus-out': () => void,
  // レイアウト完了
  'r->t:valid:complete-trim': (param: {
    kijshopCd: string,
    shopOrderId: string,
    orderId: string,
    startDate: string,
    moveMode: string,
  }) => void,
  'r->t:complete-trim': (param: {
    kijshopCd: string,
    shopOrderId: string,
    orderId: string,
    startDate: string,
    callback: () => void
  }) => void,
  // プレビュー画像の生成
  'r->t:preview:make': (param: { url: string, album: TrimPrint, callback: (list: PreviewListType) => void }) => void,
  't->r:removePreview': (param: { album: TrimPrint }) => void,
  // 余白チェッカートーストフラグ
  't->r:show-toast': (param: { isToast: boolean, target: TrimCjContainer }) => void,
  // ツールチップの表示
  't->r:show-tooltip': (param: {
    type: 'imageHover' | 'frameMove' | 'scaleIcon' | 'rotateIcon' | 'none',
    pos: { x: number, y: number }
  }) => void,
  // テキスト中のキーボードイベント制御
  'r->t:stop-keyboard-event': (isStop: boolean) => void,
  // zipファイルの取得
  't->r:zip-file:get': (param: { file: any, path: string, callback: (p: { file: any }) => void, id?: string }) => void,
  // テンプレート内で使用している写真枚数の取得
  't->r:count-photo': (param: { album: TrimPrint, photoCount: number }) => void,
  // redo/undoの進行状態の取得
  'r->t:command-list:get': (param: { listLength: number, nowIndex: number }) => void,
  // 中枠の回転
  'r->t:rotate-additional': (param: {}) => void,
  // 中枠の切り替え
  'r->t:transform-additional': (param: {}) => void,
  // 中枠の表示
  'r->t:transparent-additional': (param: { type: AdditionalShowType, callback: () => void }) => void,
  // トリミング対象フレーム
  'r->t:change-middle-frame': (param: { id: number }) => void,
  't->r:change-middle-frame': (param: { id: number }) => void,
  'r->t:shortcut:change-middle-frame': (param: { id: number, callback: (num: number) => void }) => void,
  // 人寸トリミング開始
  'r->t:trim:start': (param: {
    trimData: { id: number, windowSize: number, humanScale: number, topSpace: number, bottomSpace: number }
  }) => void,
  'r->t:shortcut:trim:start': () => void,
  // 人寸トリミング終了
  't->r:trim:end': () => void,
  // 天地線の表示
  'r->t:show-trim-line': () => void,
  'r->t:shortcut:show-trim-line': () => void,
  // ガイドの表示
  'r->t:show-guide-line': (param: { list: number[] }) => void,
  'r->t:set-trim-guide-check-list': (param: { list: number[] }) => void,
  'r->t:get-trim-guide-check-list': (param: { callback: (list: number[] | null) => void }) => void,
  'r->t:shortcut:show-guide-line': () => void,
  // ガイドの表示
  'r->t:show-select-guide': (param: { list: number[] }) => void,
  // 新規ガイド作成開始
  'r->t:create-guide:start': (param: { type: 'vertical' | 'horizontal' }) => void,
  // 新規ガイド作成完了
  't->r:create-guide:end': (param: { type: 'vertical' | 'horizontal', positionNum: number }) => void,
  // 新規ガイド削除
  'r->t:delete-guide': (param: { id: number }) => void,
  // トリミングメニューを開く
  'r->t:check:open-trim-menu': (param: { isOpen: boolean }) => void,
  // トリミングメニューを開く
  't->r:open-trim-menu': (param: { isOpen: boolean }) => void,
  'r->t:shortcut:open-trim-menu': () => void,
  // トリミングメニューを閉じる
  'r->t:close-trim-menu': () => void,
  't->r:error-close-trim-menu': () => void,
  'r->t:shortcut:close-trim-menu': () => void,
  // トリミングメニューの開閉状態の初期化
  'r->t:shortcut:trim-flag-reset': (param: { isTrimMenu: boolean }) => void,
  // ページ数変更
  't->r:open:change-page-num': (param: {
    data: CheckFinishType,
    albumSetting: AlbumSettingData,
    callback: () => void
  }) => void,
  'r->t:close:change-page-num': (param: { data: CheckFinishType }) => void,
  'r->t:shortcut:change-page-num': () => void,
  'r->t:change-cover:start': () => void,
  'r->t:change-cover:end': () => void,
  // デザイン中止
  'r->t:shortcut:design-cancel': () => void,
  // テンプレート変更履歴の表示
  'r->t:shortcut:show-temp-update': () => void,
  // ローディングポップの表示・非表示
  't->r:wait-loading': (param: { message: string }) => void,
  't->r:wait-loading2': (param: { message: string }) => void,
  't->r: post-text-pos': (param: { pos: Pos }) => void,
  // 強制レンダリング
  't->r:force-update': () => void,
  // divContainer取得
  't->r:getContainer': (param: { callback: (v: HTMLDivElement | null) => void }) => void,
  // current-albumセット
  't->r:set-album': (param: { albumPage: TrimPrint, callback: () => void }) => void,
  't->r:getOnSave': (param: { callback: (v: boolean) => void }) => void,
  't->r:back-preparation': () => void,
  // 使用画像数の更新
  't->r:change-use-image-num': (param: { type: 'increment' | 'decrement' }) => void,
  't->r:change-template': () => void,
  // レイアウトメニューのDisabled
  't->r:layout-menu-disabled': (param: { isDisabled: boolean }) => void,
  'r->t:close-button-trim-menu': () => void,
  't->r:get-shop-order-id': (callback: (shopOrderId?: string) => void) => void,
  'r->t:get-current-queue-count': (processList: Map<string, PrintUploadProgressList>) => void,
  't->r:on-create-editable-image': (param: { editableImage: TrimEditableImage }) => void,
  't->r:uploaded-editable-image': (param: { editableImage: TrimEditableImage }) => void,
};

export class TrimUiManager extends EventEmitter<EventType> implements ITrimEditorManagerBase {

  private static _ins: TrimUiManager;
  private initialized: boolean = false;

  private constructor() {
    super();
  }

  static get ins() {
    if (TrimUiManager._ins) {
      return TrimUiManager._ins;
    }
    return TrimUiManager._ins = new TrimUiManager();
  }

  initialize() {
    if (this.initialized) {
      // console.error('すでに初期化済みです !!');
      return;
    }
    this.initialized = true;
  }

  destroy(): void {
  }

  di(...manager: ITrimEditorManagerBase[]): void {
  }

  addListener(
    // event: keyof EventType | EventEmitter.EventNames<string | symbol>,
    event: keyof EventType,
    fn: EventEmitter.EventListener<EventType, any>,
    context?: any,
  ) {
    return super.addListener(event, fn, context);
  }

  removeListener(
    event: keyof EventType,
    // fn?: EventEmitter.EventListener<EventType, keyof EventType>,
    fn?: EventEmitter.EventListener<EventType, any>,
    context?: any,
    once?: boolean,
  ): this {
    return super.removeListener(event, fn, context, once);
  }
}

TrimUiManager.ins.initialize();
